import { getStateCode } from "../../../../../../constant/states";
import { UNSPECIFIED_PAYER_NAME } from "../../../../../../constant/coverages";
import { getPurePhoneNumber } from "../../../../../../utils/misc";
import { OTHER } from "../../../constants";
import { getGenericFormInitialValues } from "../../../components/form/utils/initialValues";
import { drugAssistanceType } from "../../../components/SupportOptions";
import { TEMPLATE_NAMES } from "../../../templateNames";
// sanitizeAddressLine lives in utils/validationRules.js; mirrors the authoritative FN server-side
// sanitizer (FhirBridgeService). Used here to clean the auto-filled address on prefill so the displayed
// value is already normalized (MVP-31013).
import { sanitizeAddressLine } from "../../../utils/validationRules";

// Known-payer list for the medical "Insurance Company Name" select, read from the FormJson's own optionItems so
// the rendered dropdown stays the single source of truth (drops the trailing "Other" catch-all).
const getKnownMedicalPayers = (formJson) =>
  (
    formJson.formSections
      .find((section) => section.sectionName === "insurance")
      ?.rows.flatMap((row) => row.fields || [])
      .find((field) => field.subSectionName === "medical" && field.name === "selectName")?.dedicatedFieldProps
      ?.optionItems || []
  )
    .map((option) => option.value)
    .filter((value) => value !== OTHER);

// genentech-copay now derives initial values the JSON-engine way: getGenericFormInitialValues builds every
// declared FormJson field (state ""->null, gender sanitize, phone getPurePhoneNumber + phoneType default,
// insurance UNSPECIFIED->null via cleanUnspecifiedValue, singleSelect Other via alternativeFields), plus this
// thin overlay for what the engine can't express by field type. The derivations that must stay identical to the
// pre-JSON form are pinned by initialValues.test.js in this directory.
export const getGenentechCopayFormInitialValues = ({ applicationForm, defaultFacilityNpis, formJson }) => {
  const initialValues = getGenericFormInitialValues(applicationForm, formJson);
  const formData = applicationForm?.formData;

  // MVP-31013: normalize the auto-filled patient address on prefill.
  if (initialValues.patient) {
    initialValues.patient.address = sanitizeAddressLine(initialValues.patient.address);
  }

  // The under-18 caregiver / LAP (patient.relatedPerson.*) is rendered by the unifiedCaregiver widget, not by
  // FormJson fields, so the generic engine never builds it — carry the saved block over verbatim (as the old
  // spread did) so the caregiver inputs prefill, with the LAP address sanitized (MVP-31013).
  if (formData?.patient?.relatedPerson) {
    initialValues.patient.relatedPerson = {
      ...formData.patient.relatedPerson,
      address: sanitizeAddressLine(formData.patient.relatedPerson.address)
    };
  }

  // Physician: lastName falls back to firstName; phone stripped to digits (the field is a plain `input`, so the
  // generic PHONE manipulation doesn't run); address sanitized (MVP-31013); state normalized to its 2-letter code.
  if (initialValues.physician) {
    const physician = formData?.physician;
    initialValues.physician.lastName = physician?.lastName || physician?.firstName;
    initialValues.physician.phone = physician?.phone ? getPurePhoneNumber(physician.phone) : null;
    initialValues.physician.address = sanitizeAddressLine(initialValues.physician.address);
    if (initialValues.physician.state) {
      initialValues.physician.state = getStateCode(initialValues.physician.state);
    }
  }

  // Facility: same phone/state normalization; address sanitized (MVP-31013); NPI defaults to the tenant's
  // configured genentech-copay NPI.
  if (initialValues.facility) {
    const facility = formData?.facility;
    initialValues.facility.phone = facility?.phone ? getPurePhoneNumber(facility.phone) : null;
    initialValues.facility.address = sanitizeAddressLine(initialValues.facility.address);
    if (initialValues.facility.state) {
      initialValues.facility.state = getStateCode(initialValues.facility.state);
    }
    initialValues.facility.npi =
      (defaultFacilityNpis || {})[TEMPLATE_NAMES.GENENTECH_COPAY] || initialValues.facility.npi;
  }

  // Insurance medical "Insurance Company Name" select: a saved name that's in the known-payer list preselects
  // that payer, otherwise (any other saved name) it's "Other". The generic engine's alternativeFields can only
  // yield "Other", so the known-payer branch lives here. (selectPlanType's plain planType->Other IS generic.)
  const medicalName = formData?.insurance?.medical?.name;
  if (initialValues.insurance?.medical) {
    if (medicalName && !medicalName.startsWith(UNSPECIFIED_PAYER_NAME)) {
      initialValues.insurance.medical.selectName = getKnownMedicalPayers(formJson).includes(medicalName)
        ? medicalName
        : OTHER;
    } else {
      initialValues.insurance.medical.selectName = null;
    }
  }

  // Support Options: the assistanceType checkbox group is a top-level field owned by the (nameless) supportOptions
  // widget, so the generic engine skips it — default to the always-on drug assistance type.
  initialValues.assistanceType = formData?.assistanceType ?? [drugAssistanceType];

  return initialValues;
};
